import type { Dayjs } from "dayjs";

import type {
  ExceptionEntry,
  RawFormState,
  StoredException,
  TemplateConfig,
} from "../types";

export const exceptionsToStored = (
  exceptions: ExceptionEntry[],
): StoredException[] =>
  exceptions
    .filter((e) => e.rules.size > 0)
    .map((e) => ({
      start_day: e.range[0] ? e.range[0].date() : null,
      end_day: e.range[1] ? e.range[1].date() : null,
      weekdays: e.weekdays,
      rules: Array.from(e.rules),
      remarks: e.remarks,
      applies_to: {
        type: e.appliesTo.type,
        department_ids:
          e.appliesTo.type === "department" ? e.appliesTo.departmentIds : [],
        employee_ids:
          e.appliesTo.type === "employees" ? e.appliesTo.employeeIds : [],
      },
    }));

export const formStateToTemplateConfig = (
  state: RawFormState,
): TemplateConfig => ({
  department_ids: state.deptIds,
  employee_ids: state.empIds,
  late_cutoff_time: state.lateCutoff
    ? state.lateCutoff.format("HH:mm")
    : "09:45",
  week_off_days: state.weekOffDays.length ? state.weekOffDays : [0],
  late_forgiveness_count: Math.max(0, Number(state.lateForgiveness) || 0),
  exceptions: exceptionsToStored(state.exceptions),
});

export const storedToExceptions = (
  stored: StoredException[] | undefined,
  targetMonth: Dayjs,
): ExceptionEntry[] => {
  const monthStart = targetMonth.startOf("month");
  const monthEnd = targetMonth.endOf("month");
  const daysInMonth = targetMonth.daysInMonth();
  const clampDay = (d: number | null) => {
    if (d == null) return null;
    return Math.min(Math.max(1, d), daysInMonth);
  };
  return (stored || []).map((s) => {
    const startDay = clampDay(s.start_day);
    const endDay = clampDay(s.end_day);
    const start = startDay ? monthStart.date(startDay) : monthStart;
    const end = endDay ? monthStart.date(endDay) : monthEnd;
    return {
      range: [start, end] as [Dayjs | null, Dayjs | null],
      weekdays: s.weekdays || [],
      rules: new Set(s.rules || []),
      remarks: s.remarks || "",
      appliesTo: {
        type: s.applies_to?.type || "all",
        departmentIds: s.applies_to?.department_ids || [],
        employeeIds: s.applies_to?.employee_ids || [],
      },
    };
  });
};
